import React, { useState, useEffect, useMemo } from "react";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import ButtonGroupSignoff from "protocol-setup/components/FormComponents/ButtonGroupSignoff";
import { FormTextarea, FormRadioGroup, FormFileUpload, FormCheckbox } from 'protocol-setup/components/FormComponents';
import { defaultErrorMessage, regulatorySignOffInProgress, requestValidationPath } from "protocol-setup/redux/constants";
import makeFileUploadRequest from "protocol-setup/redux/makeFileUploadRequest";
import { OpenErrorNotification, OpenSuccessNotification } from "protocol-setup/components/notification";
import { dispatchRefreshEvent, useSetupPermission } from "protocol-setup/helpers";
import { useRequestHistorySubmit } from "protocol-setup/helpers/withRequestHistory";
import { postWithAuth } from "protocol-setup/redux/makeFetch";
import { calculateFilesToDelete, handleFileDeletions } from "protocol-setup/helpers/fileHelpers";
import ProtectionElements from 'protocol-setup/helpers/ProtectionElement';
import text from "protocol-setup/constants/text";
import errorMessages, { supportedFileTypes, COMMENTS_MAX_LENGTH } from "protocol-setup/constants/errorMessages";
import PSRTable from "../PSR/PSRTable";
import { regReviewCompletedYnOptions } from "../Lovs";
import { regTabNames } from "../regHelper";

export const regulatorySignOffSchema = yup.object().shape({
  regReviewCompletedYn: yup.boolean().required("Required"),
  regComments: yup
    .string()
    .max(COMMENTS_MAX_LENGTH, errorMessages.regulatory.signOff.inProgress.regComments.max)
    .when("regReviewCompletedYn", ([regReviewCompletedYn], schema) => {
      return regReviewCompletedYn === false
        ? schema.required(errorMessages.regulatory.signOff.inProgress.regComments.required)
        : schema.optional();
    }),
  psrActions: yup.array()
    .nullable()
    .default([])
    .of(
      yup.object()
    ),
  files: yup
    .array()
    .test(
      "fileFormat",
      errorMessages.common.fileUpload.allowedFormats,
      (files) => {
        if (!files || files.length === 0) return true;
        const supportedFormats = supportedFileTypes
        return files.every(
          (file) =>
            !file ||
            supportedFormats.includes(file.name?.split(".").pop().toLowerCase())
        );
      }
    )
    .optional(),
  isRegAckCompletedYn: yup
    .boolean()
    .when('regReviewCompletedYn', ([regReviewCompletedYn], schema) => {
      if (regReviewCompletedYn === true) {
        // Require the user to check/acknowledge
        return schema
          .oneOf(
            [true],
            errorMessages.regulatory.signOff.inProgress.isRegAckCompletedYn.required
          )
          .required(
            errorMessages.regulatory.signOff.inProgress.isRegAckCompletedYn.required
          );
      }
      // Not required when review is not completed
      return schema.optional();
    })
});

export const handleSubmitForm = async (
  data,
  requestNumber,
  onSuccessCallback,
  initialFiles = []
) => {
  try {
    const fd = new FormData();
    fd.append(
      "reqStr",
      JSON.stringify({
        psrrslId: 0,
        regReviewCompletedYn: data.regReviewCompletedYn,
        regComments: data.regComments,
        requestNumber,
        notificationId: data.regReviewCompletedYn ? 'RInPrgComp' : 'RInPrgIssues',
        psrActions: data.psrActions.map((psr) => {
          return {
            psrpdId: psr.psRpdId,
            action: psr.action
          }
        }) || []
      })
    );

    // Handle file uploads
    if (Array.isArray(data.files) && data.files.length > 0) {
      data.files.forEach((file) => {
        if (file instanceof Blob || file instanceof File) {
          const { name } = file;
          fd.append("files", file, name);
        }
      });
    }

    await makeFileUploadRequest(regulatorySignOffInProgress, fd, "POST");

    const filesToDelete = calculateFilesToDelete(data.files, initialFiles);
    await handleFileDeletions(filesToDelete);

    if (onSuccessCallback) {
      await onSuccessCallback();
    }

    OpenSuccessNotification({
      message: text.regulatory.signOff.inProgress.successMessage,
      autoHide: true
    });
    return true;
  } catch (e) {
    OpenErrorNotification({
      message: defaultErrorMessage,
      autoHide: true
    });
    return false;
  }
};

const approvalLOV = [
  { label: 'Approved', value: 'APPROVED' },
  { label: 'Approved with changes', value: 'APPROVED_WITH_CHANGES' },
  { label: 'Not Approved', value: 'NOT_APPROVED' }
]
const psrActionPrefilled = (psrDetails) => {
  return psrDetails?.map((psr) => ({ ...psr, action: psr.action || 'APPROVED' })) || []
}

const RegulatoryInProgress = ({ protocolSetupData, fetchPsData, readOnly }) => {
  const { requestNumber, regSignoffLog, attachInProgress, psrDetails, regSignoff } = protocolSetupData || {};
  const {
    control,
    handleSubmit,
    reset,
    trigger,
    watch,
    formState: { isSubmitting, dirtyFields }
  } = useForm({
    resolver: yupResolver(regulatorySignOffSchema),
    defaultValues: {
      regReviewCompletedYn: regSignoffLog?.[0]?.regReviewCompletedYn || "",
      regComments: "",
      isRegAckCompletedYn: regSignoff?.isRegAckCompletedYn || false,
      files: attachInProgress || [],
      psrActions: psrActionPrefilled(psrDetails)
    }
  });

  const [showWarning, setShowWarning] = useState(false);

  const onSuccess = async () => {
    await fetchPsData();
    dispatchRefreshEvent();
  };

  const hasSignOffPermission = useSetupPermission(ProtectionElements.REGULATORY_SIGNOFF_INPROGRESS);
  const isSignOffEditable = !readOnly && hasSignOffPermission;

  const commentsLog = useMemo(() => {
    const mapped = regSignoffLog?.map((cl) => ({
      comments: cl.regComments,
      submitBy: cl.regSubmittedBy,
      submitDate: cl.regSubmittedDt?.replace(/\s+EST$/i, '')
    })) || [];

    return mapped.sort((a, b) => {
      if (!a.submitDate) return 1;
      if (!b.submitDate) return -1;
      return new Date(b.submitDate) - new Date(a.submitDate);
    });
  }, [regSignoffLog]);

  // Wrap handleSubmitForm with request history tracking
  const onSubmitWithHistory = useRequestHistorySubmit(
    (data, onSuccessCallback) =>
      handleSubmitForm(data, requestNumber, onSuccessCallback, attachInProgress),
    dirtyFields,
    regTabNames.SIGNOFF_IN_PROGRESS,
    "Signoff"
  );

  const onReset = () => {
    reset();
  };

  const validateSetup = async (data = {}) => {
    const { lpo, protocolNumber } = data || {};
    if (lpo && protocolNumber) {
      const requestBody = {
        lpo,
        protocol: protocolNumber,
        formPartType: 'OPEN_RAVE'
      };
      try {
        const response = await postWithAuth(requestValidationPath, requestBody);
        if (response && Array.isArray(response)) {
          if (response.length === 0) {
            setShowWarning(true)
          } else if (response.length > 0) {
            const warningStatuses = [
              'DRAFT',
              'SUBMITTED',
              'UAT_SETUP',
              'GROUP_TESTING',
              'CTSU_TESTING'
            ]
            const hasWarningStatus = Array.isArray(response) &&
              response.some(item => warningStatuses.includes(item?.requestStatus))
            setShowWarning(hasWarningStatus)
          } else {
            setShowWarning(false)
          }
        }
      } catch (error) {
        // catch error
      }
    }
  };

  useEffect(() => {
    if (!readOnly) {
      validateSetup(protocolSetupData);
    }
  }, [protocolSetupData, readOnly]);

  return (
    <div
      className={`form-layout-wrapper bg-transparent ${readOnly ? "read-only" : ""
        }`}
    >      <form
      id="RegPendingSignOffForm"
      className="bg-transparent mt-3"
      onSubmit={handleSubmit((data) => {
        const d = { ...data, requestNumber }
        onSubmitWithHistory(d, onSuccess)
      }
      )}
    >
        <div className="form-layout-body px-0">
          <span className="d-none"></span>
          <ul>
            {showWarning ? (
              <li className="row">
                <span className='pl-0 form-layout-message-warning col-8'>
                  {text.regulatory.signOff.submitForm.warning}
                </span>
              </li>
            ) : null}
            <li className="row mb-3">
              <div className="col-12 mb-0 form-sub-group">
                <PSRTable
                  control={control}
                  psrList={[]}
                  name="psrActions"
                  trigger={trigger}
                  readOnly={!isSignOffEditable}
                  customCols={['psrName1', 'psrType', 'ctsuProcessingInstructions', '']}
                  showAddPSR={false}
                  approvalLOV={approvalLOV}
                />
              </div>
            </li>
            <li className="row mb-0">
              <div className="col-4 mr-4 mb-0">
                <FormRadioGroup
                  name="regReviewCompletedYn"
                  section="regulatory"
                  form="regulatoryInProgress"
                  control={control}
                  required
                  options={regReviewCompletedYnOptions}
                  disabled={!isSignOffEditable}
                  trigger={trigger}
                />
              </div>
            </li>
            <li className="row my-3">
              <div className="col-8 mr-4 mb-0">
                <FormTextarea
                  name="regComments"
                  section="regulatory"
                  form="regulatoryInProgress"
                  control={control}
                  disabled={!isSignOffEditable}
                  commentsUnderLabel={commentsLog}
                  required={watch("regReviewCompletedYn") === false}
                  data-testid="form-textarea-reg-comments"
                  trigger={trigger}
                />
              </div>
            </li>
            <li className="row mb-3">
              <div className="col-12 mb-0">
                <FormFileUpload
                  name="files"
                  section="regulatory"
                  form="regulatoryInProgress"
                  control={control}
                  accept=".doc,.docx,.pdf"
                  multiple
                  disabled={!isSignOffEditable}
                />
              </div>
            </li>
            {(watch("regReviewCompletedYn") === true || !isSignOffEditable) ? (
              <li className="row">
                <div className="col-12 mb-0">
                  <FormCheckbox
                    className="pl-0 mb-3 d-flex align-items-start"
                    labelClassName="color-dark-blue align-left col-12"
                    name="isRegAckCompletedYn"
                    section="regulatory"
                    form="regulatoryInProgress"
                    required
                    control={control}
                    disabled={!isSignOffEditable}
                    readOnly={!isSignOffEditable}
                    wrapperClassName={!isSignOffEditable ? 'read-only-show' : ''}
                    trigger={trigger}
                  />
                </div>
              </li>) : null}
          </ul>
        </div>
        <div className="form-layout-footer px-0 pb-0">
          {isSignOffEditable && (
            <ButtonGroupSignoff
              onSubmit={handleSubmitForm}
              onReset={onReset}
              isSubmitting={isSubmitting}
              control={control}
              protectionElement={ProtectionElements.REGULATORY_SIGNOFF_INPROGRESS}
            />
          )}
        </div>
      </form>
    </div>
  );
};

export default RegulatoryInProgress;
